import { useState } from "react";
import { Sparkles } from "lucide-react";

interface ClarificationStepProps {
  searchTitle: string;
  checkboxes: string[];
  customInputPlaceholder: string;
  originalQuestion: string;
  onSubmit: (enhancedQuery: string) => void;
}

export function ClarificationStep({
  searchTitle,
  checkboxes,
  customInputPlaceholder,
  originalQuestion,
  onSubmit,
}: ClarificationStepProps) {
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckboxChange = (value: string) => {
    setSelectedCheckboxes((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const handleSubmit = async () => {
    const selectedAreas = [...selectedCheckboxes];
    if (customInput.trim()) selectedAreas.push(customInput.trim());

    const enhancedQuery = `${originalQuestion}. Áreas de interesse: ${selectedAreas.join(
      ", "
    )}`;

    setIsSubmitting(true);
    try {
      await onSubmit(enhancedQuery);
      setSelectedCheckboxes([]);
      setCustomInput("");
    } catch (err) {
      console.error("Error submitting clarification:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(`SKIP ${originalQuestion}`);
      setSelectedCheckboxes([]);
      setCustomInput("");
    } catch (err) {
      console.error("Error skipping clarification:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    (selectedCheckboxes.length > 0 || customInput.trim()) && !isSubmitting;

  return (
    <div className="w-full h-full px-5 py-4">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-highlight" />
        <h3 className="text-sm font-medium text-highlight">{searchTitle}</h3>
      </div>

      <div className="flex flex-wrap gap-4 mb-5">
        {checkboxes.map((option) => (
          <label
            key={option}
            className="flex items-center gap-2 text-sm cursor-pointer text-primary-text hover:text-highlight transition-colors"
          >
            <input
              type="checkbox"
              checked={selectedCheckboxes.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              disabled={isSubmitting}
              className="w-5 h-5 text-highlight border-2 border-primary-border rounded-md focus:ring-highlight focus:ring-2 disabled:opacity-50 accent-highlight appearance-none bg-white checked:bg-highlight checked:border-highlight relative checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:top-0 checked:after:left-0 checked:after:w-full checked:after:h-full checked:after:flex checked:after:items-center checked:after:justify-center"
            />
            {option}
          </label>
        ))}
      </div>

      {/* Campo de input customizado */}
      <div className="mb-5">
        <p className="text-sm text-primary-text-secondary mb-2">{customInputPlaceholder}</p>
        <input
          type="text"
          className="w-full px-3 py-2 rounded-md text-sm border bg-primary-bg border-primary-border text-primary-text placeholder:text-primary-text-secondary"
          placeholder="Por exemplo: Psicologia"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      {/* Botões de ação */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={handleSkip}
          disabled={isSubmitting}
          className="px-6 py-2 rounded-full text-sm font-medium transition text-primary-text hover:bg-primary-border hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Pular pergunta
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="px-6 py-2 rounded-full text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed bg-highlight text-white hover:opacity-90 active:scale-[0.98]"
        >
          {isSubmitting ? "Enviando..." : "Continuar"}
        </button>
      </div>
    </div>
  );
}
